/**
 * FollowersModal — list of people who follow this profile, or whom this profile
 * follows. Tapping a row routes to that person's public profile.
 *
 * Props:
 *   slug      — handle or user_id of the profile being viewed
 *   mode      — "followers" | "following"
 *   onClose   — close handler
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { api } from "../lib/api";

export default function FollowersModal({ slug, mode, onClose }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get(`/users/${slug}/${mode}`)
      .then(({ data }) => { if (!cancelled) setUsers(Array.isArray(data) ? data : []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug, mode]);

  const title = mode === "followers" ? "Followers" : "Following";

  return (
    <div
      data-testid="followers-modal"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-foreground/40 backdrop-blur-sm p-0 sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full sm:max-w-md bg-background hairline shadow-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="flex items-center justify-between px-6 py-5 hairline-b">
          <h2 className="serif text-2xl text-foreground">{title}</h2>
          <button
            data-testid="followers-modal-close"
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="serif italic text-muted-foreground p-10 text-center">Loading…</p>
          ) : users.length === 0 ? (
            <p className="serif italic text-muted-foreground p-10 text-center">
              {mode === "followers" ? "No followers yet." : "Not following anyone yet."}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {users.map((u) => {
                const targetSlug = u.handle || u.user_id;
                const initial = (u.name || "?")[0]?.toUpperCase();
                return (
                  <li key={u.user_id} data-testid={`followers-row-${u.user_id}`}>
                    <Link
                      to={`/u/${targetSlug}`}
                      onClick={onClose}
                      className="flex items-center gap-4 px-6 py-4 hover:bg-secondary transition-colors"
                    >
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-10 h-10 rounded-full ring-1 ring-border flex-shrink-0" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-[hsl(var(--accent)/0.35)] grid place-items-center serif text-sm text-foreground flex-shrink-0">
                          {initial}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="serif text-foreground truncate">{u.name}</div>
                        {u.handle && (
                          <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground truncate">
                            @{u.handle}
                          </div>
                        )}
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
